/**
 * RequireAuth — route guard that redirects unauthenticated requests to /login.
 *
 * Reads from `useAuth()` (which mirrors `sessionStorage["portal_jwt"]`)
 * rather than `useAuthStore` from `@tensaw/runtime` — the JWT is the
 * source of truth (chatbot widget, refresh-survival, etc.), and the
 * Zustand store in `@tensaw/runtime` is shaped for the staff app's needs.
 *
 * The current path + search is preserved as `?next=` so the patient lands
 * back where they started after signing in. SMS deep-links benefit from
 * this — e.g. /checkin/88001 → /login?next=/checkin/88001 → /checkin/88001.
 *
 * It also mounts the `<tensaw-chatbot>` custom element after authentication,
 * passing claims and token properties securely to ensure state and context
 * are correctly set up.
 */
import { useCallback, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { useAuth } from './AuthContext';
import { type PortalJWTClaims } from '../lib/jwt';

// Declare the custom element in the JSX namespace for TypeScript compiler compatibility
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'tensaw-chatbot': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'tenant-key'?: string;
        'sub-tenant-key'?: string;
        'clinic-id'?: string;
        env?: string;
        'orchestration-url'?: string;
      };
    }
  }
}

// Derive the chatbot environment from the orchestration URL config, NOT from the
// JWT's `env` claim. The JWT `env` reflects the *portal backend* environment
// (e.g. "dev" when running a local backend), which may differ from the chatbot
// backend environment (e.g. "staging"). Using the orchestration URL as the
// source of truth ensures the two always agree.
function deriveEnvFromOrchestrationUrl(url: string | undefined): string {
  if (!url) return 'staging';
  if (url.includes('staging')) return 'staging';
  if (url.includes('production') || url.includes('prod')) return 'production';
  return 'staging';
}

type ChatbotElement = HTMLElement & { portalJwt: string | null };

export function RequireAuth() {
  const { isAuthenticated, jwt } = useAuth();
  const location = useLocation();

  // Keep the latest JWT in a ref so the ref-callback always sees current value.
  const jwtRef = useRef<string | null>(jwt ?? null);
  jwtRef.current = jwt ?? null;

  // Decode claims from JWT to pass as props to the chatbot custom element.
  let tenantKey: string | undefined;
  let clinicId: string | undefined;

  if (jwt) {
    try {
      const claims = jwtDecode<PortalJWTClaims>(jwt);
      tenantKey = claims.tid;
      clinicId = claims.cid;
      // Note: we intentionally do NOT use claims.env here — see deriveEnvFromOrchestrationUrl.
    } catch (err) {
      console.error('[patient-portal] Failed to decode JWT for chatbot config:', err);
    }
  }

  const orchestrationUrl = import.meta.env.VITE_CHATBOT_ORCHESTRATION_URL as string | undefined;

  // Derive the chatbot env from the configured orchestration URL, not the JWT claim.
  // This prevents a local-dev JWT with env:"dev" from being sent to the staging chatbot.
  const chatbotEnv = deriveEnvFromOrchestrationUrl(orchestrationUrl);

  // Use a ref callback instead of useRef + useEffect.
  // When React mounts the <tensaw-chatbot> element into the DOM, this callback fires
  // synchronously. Setting el.portalJwt here ensures _portalJwt is populated on the
  // TensawChatbotElement instance BEFORE connectedCallback runs, so the React app
  // inside the shadow DOM receives the token at initial mount (not in a later effect).
  const chatbotRefCallback = useCallback(
    (el: ChatbotElement | null) => {
      if (!el) return;
      // Set token immediately — element.ts setter stores it in _portalJwt if the
      // React tree hasn't mounted yet, which connectedCallback then picks up.
      el.portalJwt = jwtRef.current;
      console.log(
        '[patient-portal] chatbot ref callback: portalJwt set to',
        jwtRef.current ? 'PRESENT' : 'MISSING',
      );
    },
    // Empty deps: this callback's identity is stable; jwtRef.current is read
    // at call time, not captured at definition time.
    [],
  );

  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return (
    <>
      <Outlet />
      <tensaw-chatbot
        ref={chatbotRefCallback as unknown as React.RefCallback<HTMLElement>}
        tenant-key={tenantKey}
        clinic-id={clinicId}
        env={chatbotEnv}
        orchestration-url={orchestrationUrl || undefined}
      />
    </>
  );
}
